import React from "react";
import { ArrowRight, Compass, Headphones, Calendar } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { SITE, buildWhatsAppLink } from "../data/static";

const ICONS = [Compass, Calendar, Headphones];

const Feature = ({ Icon, title, desc }) => (
  <div className="flex items-start gap-4">
    <div className="w-11 h-11 rounded-2xl bg-white text-[#D1603D] border border-[#D1603D]/20 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <p className="font-display text-lg font-semibold text-slate-900 leading-tight">{title}</p>
      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{desc}</p>
    </div>
  </div>
);

export const CustomItinerary = () => {
  const { t } = useI18n();
  const c = t.custom;
  const link = buildWhatsAppLink(c.waMsg);

  return (
    <section id="custom" data-testid="custom-itinerary-section" className="relative py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#FADBD1] via-[#FAFAF7] to-[#FDE68A]/40 border border-[#D1603D]/15 p-6 sm:p-10 lg:p-16">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#D1603D]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-[#FDE68A]/40 blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D1603D]">{c.eyebrow}</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-slate-900 leading-[1.05]">
                {c.title} <span className="italic text-[#D1603D]">{c.titleItalic}</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-xl">{c.body}</p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href={link} target="_blank" rel="noopener noreferrer" data-testid="custom-whatsapp-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#0F172A] text-white text-sm font-semibold hover:bg-[#D1603D] transition-colors active:scale-95 group">
                  {c.cta1}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a href={`tel:+${SITE.whatsappNumber}`} data-testid="custom-call-btn"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white border border-slate-200 text-slate-900 text-sm font-semibold hover:border-slate-900 transition-colors active:scale-95">
                  {c.cta2} {SITE.phoneDisplay}
                </a>
              </div>
            </div>

            <div className="grid gap-5 sm:gap-6">
              {c.features.map((f, idx) => (
                <Feature key={f.title} Icon={ICONS[idx % ICONS.length]} title={f.title} desc={f.desc} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomItinerary;
